import { z } from "zod";
import { buildNodeGrid } from "../routing/autoPipeRouting/buildNodeGrid.logic";
// ---------------------------------------------------------------------------
// Existing numbering utilities
// ---------------------------------------------------------------------------
export const ROOM_NUMBERING_METHOD_KEYS = ["NRH", "DRAMMEN_SYKEHUS"];
export const ZRoomNumberingMethodKey = z.enum(ROOM_NUMBERING_METHOD_KEYS);
export const ZRoomNumberingMethod = z.object({
    key: ZRoomNumberingMethodKey,
    title: z.string().min(1),
    customer: z.string().min(1),
    pdfFileName: z.string().min(1),
    summary: z.string().min(1).optional(),
});
export const ROOM_NUMBERING_METHODS = [
    {
        key: "NRH",
        title: "NRH romnummerering",
        customer: "Sykehusbygg HF",
        pdfFileName: "NRH-FAKE-ROMNUMMERERING.pdf",
        summary: "Skall for NRH romnummerering.",
    },
    {
        key: "DRAMMEN_SYKEHUS",
        title: "Drammen sykehus romnummerering",
        customer: "Sykehusbygg HF",
        pdfFileName: "NSD-8201-A-NO-0047 Romnummerering.pdf",
        summary: "Romnummerering basert på etasje, trapperom, korridoravstand og side av hovedakse.",
    },
];
export const ZRoomNumberingMethods = z.array(ZRoomNumberingMethod);
/** Staircase letters — I is excluded to avoid confusion with 1. */
export const STAIRCASE_LETTERS = "ABCDEFGHJKLMNOPQRSTUVWXYZ";
function nextRoomNumber(from, step, reserved) {
    let n = from;
    do {
        n += step;
    } while (n % 10 === 0 || n % 10 === 5 || reserved.has(n));
    return n;
}
/** Returns the next even room number after `from`, skipping multiples of 5/10 and reserved numbers. */
export function nextEvenRoomNumber(from, reserved = new Set()) {
    return nextRoomNumber(from, 2, reserved);
}
/** Returns the next odd room number after `from`, skipping multiples of 5/10 and reserved numbers. */
export function nextOddRoomNumber(from, reserved = new Set()) {
    return nextRoomNumber(from, 2, reserved);
}
/**
 * Two-pass assignment over vertical clusters for a given number range.
 *
 * Pass 1: clusters with an approved or manual entry (from the lowest floor, since
 *         clusters are built lowest-floor-first) are resolved immediately and their
 *         numbers recorded as taken.
 * Pass 2: remaining clusters receive the next sequential number in [rangeStart, rangeEnd]
 *         that hasn't been claimed by a fixed entry.
 */
export function assignClustersFromRange(clusters, currentFloorIndex, rangeStart, rangeEnd) {
    const output = {};
    const taken = new Set();
    const pending = [];
    for (const cluster of clusters) {
        const currentFloorRooms = cluster.rooms.filter((e) => e.floorIndex === currentFloorIndex);
        if (currentFloorRooms.length === 0)
            continue;
        // Priority 1: approved entry on lowest floor in cluster
        const approvedEntry = cluster.rooms.find((e) => e.room.approved && e.room.manualRoomNumber?.trim());
        if (approvedEntry) {
            const num = approvedEntry.room.manualRoomNumber.trim();
            const n = extractRoomNumberIndex(num);
            if (n != null)
                taken.add(n);
            for (const e of currentFloorRooms)
                output[e.room.roomNumber] = num;
            continue;
        }
        // Priority 2: manual entry on lowest floor in cluster
        const manualEntry = cluster.rooms.find((e) => e.room.manualRoomNumber?.trim());
        if (manualEntry) {
            const num = manualEntry.room.manualRoomNumber.trim();
            const n = extractRoomNumberIndex(num);
            if (n != null)
                taken.add(n);
            for (const e of currentFloorRooms)
                output[e.room.roomNumber] = num;
            continue;
        }
        pending.push({ cluster, currentFloorRooms });
    }
    let autoLast = rangeStart - 1;
    for (const { currentFloorRooms } of pending) {
        let next = autoLast + 1;
        while (next <= rangeEnd && taken.has(next))
            next++;
        if (next > rangeEnd)
            continue;
        autoLast = next;
        taken.add(next);
        for (const e of currentFloorRooms)
            output[e.room.roomNumber] = next.toString();
    }
    return output;
}
// ---------------------------------------------------------------------------
// Polygon helpers
// ---------------------------------------------------------------------------
export function polygonArea(polygon) {
    const pts = isClosedRing(polygon) ? polygon.slice(0, -1) : polygon;
    const n = pts.length;
    let area = 0;
    for (let i = 0; i < n; i++) {
        const [x1, y1] = pts[i];
        const [x2, y2] = pts[(i + 1) % n];
        area += x1 * y2 - x2 * y1;
    }
    return Math.abs(area) / 2;
}
export function polygonCentroid(polygon) {
    let sx = 0;
    let sy = 0;
    const pts = isClosedRing(polygon) ? polygon.slice(0, -1) : polygon;
    for (const [x, y] of pts) {
        sx += x;
        sy += y;
    }
    return [sx / pts.length, sy / pts.length];
}
export function isClosedRing(polygon) {
    if (polygon.length < 2)
        return false;
    const first = polygon[0];
    const last = polygon[polygon.length - 1];
    return Math.abs(first[0] - last[0]) < 1e-9 && Math.abs(first[1] - last[1]) < 1e-9;
}
function pointToLineDistanceSq(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-18) {
        const ex = px - ax, ey = py - ay;
        return ex * ex + ey * ey;
    }
    const cross = (px - ax) * dy - (py - ay) * dx;
    return (cross * cross) / lenSq;
}
function projectOntoSegmentT(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-18)
        return 0;
    return ((px - ax) * dx + (py - ay) * dy) / lenSq;
}
function colinearOverlapLength(a0x, a0y, a1x, a1y, b0x, b0y, b1x, b1y) {
    const lenA = Math.hypot(a1x - a0x, a1y - a0y);
    if (lenA < 1e-9)
        return 0;
    const tB0 = projectOntoSegmentT(b0x, b0y, a0x, a0y, a1x, a1y);
    const tB1 = projectOntoSegmentT(b1x, b1y, a0x, a0y, a1x, a1y);
    const tMin = Math.max(0, Math.min(tB0, tB1));
    const tMax = Math.min(1, Math.max(tB0, tB1));
    if (tMax <= tMin)
        return 0;
    return (tMax - tMin) * lenA;
}
function sharedWallLength(polyA, polyB) {
    const edgesA = polygonEdges(polyA);
    const edgesB = polygonEdges(polyB);
    let maxOverlap = 0;
    const tolSq = ADJACENCY_TOLERANCE * ADJACENCY_TOLERANCE;
    for (const [a0, a1] of edgesA) {
        const [a0x, a0y] = a0, [a1x, a1y] = a1;
        if ((a1x - a0x) ** 2 + (a1y - a0y) ** 2 < 1e-9)
            continue;
        for (const [b0, b1] of edgesB) {
            const [b0x, b0y] = b0, [b1x, b1y] = b1;
            if ((b1x - b0x) ** 2 + (b1y - b0y) ** 2 < 1e-9)
                continue;
            if (pointToLineDistanceSq(b0x, b0y, a0x, a0y, a1x, a1y) > tolSq ||
                pointToLineDistanceSq(b1x, b1y, a0x, a0y, a1x, a1y) > tolSq)
                continue;
            const overlap = colinearOverlapLength(a0x, a0y, a1x, a1y, b0x, b0y, b1x, b1y);
            if (overlap > maxOverlap)
                maxOverlap = overlap;
        }
    }
    return maxOverlap >= MIN_SHARED_WALL_LENGTH ? maxOverlap : 0;
}
function polygonEdges(polygon) {
    const edges = [];
    const n = polygon.length;
    for (let i = 0; i < n - 1; i++)
        edges.push([polygon[i], polygon[i + 1]]);
    if (!isClosedRing(polygon))
        edges.push([polygon[n - 1], polygon[0]]);
    return edges;
}
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const ADJACENCY_TOLERANCE = 0.5;
const MIN_SHARED_WALL_LENGTH = 0.05;
export const GRID_RESOLUTION = 0.5;
export const PENALTY_WALL = 1000;
export const PENALTY_DOOR_ROOM = 100;
const DOOR_DISTANCE_THR = 0.5;
const DOOR_PERPENDICULAR_DOT_THR = 0.5;
function cross2D(ax, ay, bx, by) {
    return ax * by - ay * bx;
}
/** Returns true if segment a1–a2 and segment b1–b2 properly cross (not at endpoints). */
export function segmentsCross(a1, a2, b1, b2) {
    const d1x = a2[0] - a1[0], d1y = a2[1] - a1[1];
    const d2x = b2[0] - b1[0], d2y = b2[1] - b1[1];
    const denom = cross2D(d1x, d1y, d2x, d2y);
    if (Math.abs(denom) < 1e-10)
        return false; // parallel / collinear
    const d3x = b1[0] - a1[0], d3y = b1[1] - a1[1];
    const t = cross2D(d3x, d3y, d2x, d2y) / denom;
    const u = cross2D(d3x, d3y, d1x, d1y) / denom;
    return t > 0 && t < 1 && u > 0 && u < 1;
}
// ---------------------------------------------------------------------------
// Room classification
// ---------------------------------------------------------------------------
export function classifyRoom(roomName) {
    if (!roomName)
        return "room";
    const lower = roomName.toLowerCase();
    if (/heis|elevator|lift/.test(lower))
        return "elevator";
    if (/trapp|stair/.test(lower))
        return "staircase";
    if (/sjakt/.test(lower))
        return "shaft";
    if (/forrom/.test(lower))
        return "numbered_corridor";
    if (/sluse|sluice|korridor|corridor|g[åa]ng|gang|vestibyle|nisje/.test(lower))
        return "corridor";
    if (/sjakt|vvs|varmesentral|varmepumpe|varmeentr|elkraft|el[- ]?tavle|tavlerom|\bikt\b|serverrom|datarom|h[øo]yspent|trafo|transformator|teknisk[t]? rom/.test(lower))
        return "technical";
    return "room";
}
// ---------------------------------------------------------------------------
// Room graph
// ---------------------------------------------------------------------------
export function buildRoomGraph(roomsOnFloor) {
    const nodes = roomsOnFloor.map((room, index) => ({
        index,
        roomNumber: room.roomNumber,
        type: classifyRoom(room.roomName),
        centroid: polygonCentroid(room.polygon),
    }));
    const edges = [];
    for (let i = 0; i < roomsOnFloor.length; i++) {
        for (let j = i + 1; j < roomsOnFloor.length; j++) {
            const overlap = sharedWallLength(roomsOnFloor[i].polygon, roomsOnFloor[j].polygon);
            if (overlap > 0)
                edges.push({ a: i, b: j, sharedWallLength: overlap });
        }
    }
    return { nodes, edges };
}
// ---------------------------------------------------------------------------
// Room adjacency from routing grid
// ---------------------------------------------------------------------------
export function buildRoomAdjacencyFromGrid(graph) {
    const roomAdj = new Map();
    const nodesById = new Map(graph.nodes.map((n) => [n.id, n]));
    const addAdjacency = (r1, r2) => {
        if (r1 === r2)
            return;
        if (!roomAdj.has(r1))
            roomAdj.set(r1, new Set());
        if (!roomAdj.has(r2))
            roomAdj.set(r2, new Set());
        roomAdj.get(r1).add(r2);
        roomAdj.get(r2).add(r1);
    };
    const nodeToVisibleRooms = new Map();
    for (const node of graph.nodes)
        nodeToVisibleRooms.set(node.id, new Set(node.rooms ?? []));
    for (const edge of graph.edges) {
        const roomsU = nodesById.get(edge.from)?.rooms ?? [];
        const roomsV = nodesById.get(edge.to)?.rooms ?? [];
        const setU = nodeToVisibleRooms.get(edge.from);
        const setV = nodeToVisibleRooms.get(edge.to);
        if (setU)
            for (const r of roomsV)
                setU.add(r);
        if (setV)
            for (const r of roomsU)
                setV.add(r);
    }
    for (const rooms of nodeToVisibleRooms.values()) {
        const roomList = Array.from(rooms);
        for (let i = 0; i < roomList.length; i++)
            for (let j = i + 1; j < roomList.length; j++)
                addAdjacency(roomList[i], roomList[j]);
    }
    return roomAdj;
}
// ---------------------------------------------------------------------------
// Room numbering grid
// ---------------------------------------------------------------------------
export function buildRoomNumberingGrid(rooms, floorNumber = 0) {
    const { graph } = buildNodeGrid({
        projectNo: 0,
        floorName: `floor-${floorNumber}`,
        selectedRooms: rooms.map((r) => ({ roomNumber: r.roomNumber, polygon: r.polygon })),
        devicesToConnect: [],
        connectionPoints: [[0, 0, 0]],
        pipeElevationsM: { xDirection: 0, yDirection: 1 },
    }, GRID_RESOLUTION);
    return graph;
}
// ---------------------------------------------------------------------------
// Door crossing check
// ---------------------------------------------------------------------------
export function edgeCrossesDoor(a, b, door) {
    const pts = door.bbox;
    if (pts.length < 2)
        return false;
    const p0 = pts[0];
    const p1 = pts[1];
    const p2 = pts[2];
    const e1x = p1[0] - p0[0], e1y = p1[1] - p0[1];
    const e2x = p2[0] - p1[0], e2y = p2[1] - p1[1];
    const len1 = Math.hypot(e1x, e1y), len2 = Math.hypot(e2x, e2y);
    if (len1 < 1e-9 || len2 < 1e-9)
        return false;
    const [lx, ly, longLen, sx, sy, shortLen] = len1 >= len2
        ? [e1x / len1, e1y / len1, len1, e2x / len2, e2y / len2, len2]
        : [e2x / len2, e2y / len2, len2, e1x / len1, e1y / len1, len1];
    const cx = (p0[0] + pts[Math.floor(pts.length / 2)][0]) / 2;
    const cy = (p0[1] + pts[Math.floor(pts.length / 2)][1]) / 2;
    const edgeLen = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (edgeLen < 1e-9)
        return false;
    const edx = (b[0] - a[0]) / edgeLen, edy = (b[1] - a[1]) / edgeLen;
    if (Math.abs(edx * lx + edy * ly) > DOOR_PERPENDICULAR_DOT_THR)
        return false;
    const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
    if (Math.abs((mx - cx) * lx + (my - cy) * ly) > longLen / 2)
        return false;
    const projShort = Math.abs((mx - cx) * sx + (my - cy) * sy);
    return Math.max(0, projShort - shortLen / 2) < DOOR_DISTANCE_THR;
}
// ---------------------------------------------------------------------------
// Room numbering adjacency
// ---------------------------------------------------------------------------
export function buildRoomNumberingAdjacency(graph, rooms, doors = [], walls = []) {
    const roomTypeMap = new Map(rooms.map((r) => [r.roomNumber, classifyRoom(r.roomName)]));
    const getNodeMainType = (nodeRooms) => {
        if (nodeRooms.size === 0)
            return "outside";
        const types = Array.from(nodeRooms).map((r) => roomTypeMap.get(r));
        if (types.includes("staircase"))
            return "staircase";
        if (types.includes("corridor"))
            return "corridor";
        if (types.includes("numbered_corridor"))
            return "numbered_corridor";
        if (types.includes("room"))
            return "room";
        if (types.includes("technical"))
            return "technical";
        return "room";
    };
    const infraTypes = ["staircase", "corridor", "numbered_corridor"];
    const adj = new Map();
    for (const node of graph.nodes)
        adj.set(node.id, []);
    const pos = new Map();
    const roomSets = new Map();
    for (const node of graph.nodes) {
        pos.set(node.id, [node.posM[0], node.posM[1]]);
        roomSets.set(node.id, new Set(node.rooms ?? []));
    }
    for (const edge of graph.edges) {
        const a = pos.get(edge.from), b = pos.get(edge.to);
        // Skip edges that a user-placed wall bisects — treat as impassable.
        if (walls.some((w) => segmentsCross(a, b, w.start, w.end)))
            continue;
        const dx = a[0] - b[0], dy = a[1] - b[1];
        const distM = Math.hypot(dx, dy);
        let cost = distM || 0.0001;
        const roomsA = roomSets.get(edge.from);
        const roomsB = roomSets.get(edge.to);
        const typeA = getNodeMainType(roomsA);
        const typeB = getNodeMainType(roomsB);
        const isWallCrossing = !(roomsA.size > 0 && roomsB.size > 0 && [...roomsA].every((r) => roomsB.has(r)) && roomsA.size === roomsB.size);
        const crossesDoor = isWallCrossing && doors.length > 0 && doors.some((door) => edgeCrossesDoor(a, b, door));
        let penalty = 0;
        if (isWallCrossing) {
            const aIsInfra = infraTypes.includes(typeA);
            const bIsInfra = infraTypes.includes(typeB);
            if (crossesDoor) {
                if (aIsInfra && bIsInfra)
                    penalty = 0;
                else if ((typeA === "corridor" && typeB === "room") || (typeB === "corridor" && typeA === "room"))
                    penalty = 0;
                else
                    penalty = PENALTY_DOOR_ROOM;
            }
            else {
                penalty = (aIsInfra && bIsInfra) ? 0 : PENALTY_WALL;
            }
        }
        cost += penalty;
        adj.get(edge.from)?.push({ neighbor: edge.to, edgeId: edge.id, cost, distM });
        adj.get(edge.to)?.push({ neighbor: edge.from, edgeId: edge.id, cost, distM });
    }
    return adj;
}
// ---------------------------------------------------------------------------
// Room number parsing
// ---------------------------------------------------------------------------
export function extractRoomNumberIndex(tag) {
    const match = tag.match(/\.\s*(\d+)[A-Z]?$/i);
    if (!match)
        return null;
    const n = parseInt(match[1], 10);
    return isNaN(n) ? null : n;
}
// ---------------------------------------------------------------------------
// Debug download
// ---------------------------------------------------------------------------
export function debugDownloadRoomNumberingGrid(grid, distMap, filename = "room-numbering-grid.json") {
    const annotatedGrid = distMap
        ? { ...grid, nodes: grid.nodes.map((n) => ({ ...n, distToStaircase: distMap.get(n.id)?.dist ?? null })) }
        : grid;
    const json = JSON.stringify(annotatedGrid, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}
